// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <https://benrichardson.dev>
// Based on QuakeWatch by Ben Richardson — https://benrichardson.dev

/** Locale-formatted integer with thousands separators. Non-finite → "—". */
export function num(n: number): string {
  if (!Number.isFinite(n)) return '—';
  return Math.round(n).toLocaleString('en-US');
}

/** Fixed-decimal number, non-finite → "—". */
export function dec(n: number, places = 1): string {
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('en-US', { minimumFractionDigits: places, maximumFractionDigits: places });
}

/** Magnitude to one decimal place, always signed-safe (e.g. "4.2", "0.3"). */
export function mag(n: number): string {
  if (!Number.isFinite(n)) return '—';
  return n.toFixed(1);
}

/** Depth in km, tidy. */
export function depthKm(n: number): string {
  if (!Number.isFinite(n)) return '—';
  if (n < 10) return `${n.toFixed(1)} km`;
  return `${Math.round(n)} km`;
}

/** Relative time like "3m ago", "2h ago", "Just now". `now` injectable for tests. */
export function relTime(ms: number, now: number = Date.now()): string {
  const diff = now - ms;
  if (!Number.isFinite(diff)) return '—';
  if (diff < 0) return 'just now';
  const s = Math.floor(diff / 1000);
  if (s < 45) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

/** Absolute UTC time, compact: "31 Jul 2026, 14:05 UTC". */
export function absTime(ms: number): string {
  if (!Number.isFinite(ms)) return '—';
  const d = new Date(ms);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getUTCDate()} ${months[d.getUTCMonth()]} ${d.getUTCFullYear()}, ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())} UTC`;
}

/**
 * Human-readable energy: joules → a compact "×10ⁿ J" or comparison string.
 * Returns e.g. "6.3 × 10¹² J".
 */
export function joules(j: number): string {
  if (!Number.isFinite(j) || j <= 0) return '—';
  const exp = Math.floor(Math.log10(j));
  const mantissa = j / Math.pow(10, exp);
  const sup = toSuperscript(exp);
  return `${mantissa.toFixed(1)} × 10${sup} J`;
}

/** TNT-equivalent tonnage from joules (1 t TNT = 4.184e9 J), tidy with units. */
export function tnt(j: number): string {
  if (!Number.isFinite(j) || j <= 0) return '—';
  const tonnes = j / 4.184e9;
  if (tonnes < 0.001) return `${(tonnes * 1e6).toFixed(0)} g of TNT`;
  if (tonnes < 1) return `${(tonnes * 1000).toFixed(0)} kg of TNT`;
  if (tonnes < 1000) return `${tonnes.toFixed(1)} tonnes of TNT`;
  if (tonnes < 1e6) return `${num(tonnes / 1000)} kt of TNT`;
  return `${num(tonnes / 1e6)} Mt of TNT`;
}

export function toSuperscript(n: number): string {
  const map: Record<string, string> = {
    '0': '⁰', '1': '¹', '2': '²', '3': '³', '4': '⁴',
    '5': '⁵', '6': '⁶', '7': '⁷', '8': '⁸', '9': '⁹', '-': '⁻',
  };
  return String(n).split('').map((c) => map[c] ?? c).join('');
}

/** Escape text destined for innerHTML. */
export function esc(s: string): string {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Escape text destined for an HTML attribute value that a browser will later
 * turn into a tooltip via `data-tip`. Real newlines are preserved as `&#10;`
 * so multi-line tips render; used with setAttribute-free innerHTML building.
 */
export function escAttr(s: string): string {
  return esc(s).replace(/\n/g, '&#10;');
}
